"use client";
/**
 * Home next-best-offer carousel (Block 4, overview 7.6 "Home screen").
 *
 * A horizontal, snap-scrolling rail of up to 3 ranked offers (surface "home").
 * Each card is dismissable per-session (sessionStorage); "View" deep-links to
 * the menu with the code pre-applied for code-based offers, otherwise it opens
 * the offers inbox.
 *
 * Renders nothing unless signed in AND the ranker returns at least one
 * not-yet-dismissed offer — so it never adds empty chrome to Home.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { OfferIcon, offerUseNowPath } from "@/components/offers/OfferBits";
import { useNextBestOffers, useSessionDismissal } from "@/hooks/useOffers";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { track } from "@/lib/analytics";
import type { NextBestOffer } from "@/api/services";

export function HomeOffersCarousel() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { branchId, branchSlug } = useBranch();
  const { offers } = useNextBestOffers("home", { branchId });
  const { isDismissed, dismiss } = useSessionDismissal(branchSlug);

  const visible = useMemo(
    () => offers.filter((o) => !isDismissed(`home:${o.promotionId}`)).slice(0, 3),
    [offers, isDismissed],
  );

  if (!isLoggedIn || visible.length === 0) return null;

  const onView = (offer: NextBestOffer) => {
    track("offer_view", { promotion_id: offer.promotionId, surface: "home" });
    if (offer.code) {
      navigate(offerUseNowPath(offer.code));
    } else {
      navigate("/offers");
    }
  };

  return (
    <div style={{ padding: "12px 0 4px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 16px 10px",
        }}
      >
        <Icons.ticket size={16} style={{ color: "var(--primary-700)" }} />
        <span className="display" style={{ fontSize: 14 }}>
          {t("v2.offers.home_rail_title")}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          padding: "0 16px 4px",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {visible.map((offer) => (
          <div
            key={offer.promotionId}
            role="button"
            tabIndex={0}
            onClick={() => onView(offer)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onView(offer);
            }}
            style={{
              position: "relative",
              flex: "0 0 78%",
              maxWidth: 300,
              scrollSnapAlign: "start",
              background: "var(--card-bg)",
              border: "var(--card-border)",
              boxShadow: "var(--card-shadow)",
              borderRadius: "var(--radius-card)",
              padding: 14,
              cursor: "pointer",
            }}
          >
            {/* Per-card dismiss */}
            <button
              type="button"
              aria-label={t("v2.offers.dismiss")}
              onClick={(e) => {
                e.stopPropagation();
                track("offer_dismissed", { promotion_id: offer.promotionId, surface: "home" });
                dismiss(`home:${offer.promotionId}`);
              }}
              style={{
                position: "absolute",
                top: 8,
                insetInlineEnd: 8,
                width: 26,
                height: 26,
                borderRadius: 999,
                border: "1px solid var(--border)",
                background: "var(--surface-1)",
                color: "var(--ink-3)",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
              }}
            >
              <Icons.close size={13} />
            </button>

            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", paddingInlineEnd: 22 }}>
              <OfferIcon rewardKind={offer.rewardKind} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="display"
                  style={{
                    fontSize: 14,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {offer.headline}
                </div>
                {offer.description && (
                  <div
                    className="muted"
                    style={{
                      fontSize: 12,
                      marginTop: 2,
                      lineHeight: 1.4,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {offer.description}
                  </div>
                )}
                <div
                  style={{
                    marginTop: 10,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    color: "var(--primary-700)",
                    fontWeight: 700,
                    fontSize: 12.5,
                  }}
                >
                  {t("v2.offers.view")}
                  <Icons.chevron size={13} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
